/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { IncomingHttpHeaders } from 'http';
import type { ForteData, ForteFlagValue, ForteFlags, ForteFlagName } from '../interface';

type BooleanForteFlagName = {
  [K in ForteFlagName]: ForteFlags[K] extends boolean | undefined ? K : never;
}[ForteFlagName];

const FT_HEADER_PREFIX = 'ft-';

const kebabToCamelCase = (value: string) =>
  value.replace(/-([a-z0-9])/g, (_, char: string) => char.toUpperCase());

const parseForteHeaderValue = (value: string): ForteFlagValue => {
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  const asNumber = Number(value);
  if (!isNaN(asNumber) && value.trim() !== '') {
    return asNumber;
  }
  return value;
};

const buildForteDataFromHeaders = (headers: IncomingHttpHeaders): ForteData => {
  const flags: Record<string, ForteFlagValue> = {};

  for (const [headerName, headerValue] of Object.entries(headers)) {
    if (!headerName.startsWith(FT_HEADER_PREFIX) || headerValue === undefined) {
      continue;
    }

    const flagKey = kebabToCamelCase(headerName.slice(FT_HEADER_PREFIX.length));
    const rawValue = Array.isArray(headerValue) ? headerValue[0] : headerValue;
    flags[flagKey] = parseForteHeaderValue(rawValue);
  }

  return {
    flags,
  };
};

const getForteDecisionFromCache = (queryClient: QueryClient) => {
  return queryClient.getQueryData<ForteData>(getForteDecisionKey());
};

const selectValue = <F extends ForteFlagName>(
  data: ForteData | undefined,
  flag: F,
): ForteFlags[F] | undefined => {
  if (!data || !(flag in data.flags)) {
    return undefined;
  }

  return data.flags[flag as string] as ForteFlags[F];
};

/**
 * Get the value of a Forte flag.
 *
 * Important: Always use `useForteDecisionFlag` inside React components.
 * This prevents the query data being garbage collected,
 * and allows local cache updates to be reflected in the UI.
 */
const getForteDecisionFlag = <F extends ForteFlagName>(
  queryClient: QueryClient,
  flag: F,
): ForteFlags[F] | undefined => {
  const forteDecisionData = getForteDecisionFromCache(queryClient);

  return selectValue(forteDecisionData, flag);
};

const initialData: ForteData = {
  flags: {},
};

/**
 * Get the value of a Forte flag.
 */
export const useForteDecisionFlag = <F extends ForteFlagName>(
  flag: F,
): ForteFlags[F] | undefined => {
  const select = useCallback((data: ForteData) => selectValue(data, flag), [flag]);
  const { data: decisionFlag } = useQuery({
    queryKey: getForteDecisionKey(),
    queryFn: () => {
      throw new Error('Query prefetch failed');
    },
    select,
    initialData,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return decisionFlag as ForteFlags[F] | undefined;
};

const applyTrueDefault = (flagValue: boolean | undefined): boolean => flagValue ?? true;

/**
 * True-default variant of the Forte flag accessors: returns `true` when the flag is absent,
 * `false` when explicitly disabled, and `true` when explicitly enabled.
 *
 * Use these instead of the base flag accessors for features that should be on by default.
 */
export const useForteDecisionFlagWithTrueDefault = <F extends BooleanForteFlagName>(
  flag: F,
): boolean => applyTrueDefault(useForteDecisionFlag(flag) as boolean | undefined);

const getForteDecisionKey = () => ['forte'] as const;

const prefetchForteDecision = ({
  queryClient,
  headers,
}: {
  queryClient: QueryClient;
  headers: IncomingHttpHeaders;
}) => {
  queryClient.setQueryData(getForteDecisionKey(), buildForteDataFromHeaders(headers));
};

const getForteDecisionFlagWithTrueDefault = <F extends BooleanForteFlagName>(
  queryClient: QueryClient,
  flag: F,
): boolean => applyTrueDefault(getForteDecisionFlag(queryClient, flag) as boolean | undefined);

export {
  getForteDecisionKey,
  getForteDecisionFromCache,
  prefetchForteDecision,
  getForteDecisionFlag,
  getForteDecisionFlagWithTrueDefault,
};
